/**
 * One small dialog for the Home scene's folder questions and the draft name.
 *
 * Two shapes, one component — the web's `PromptDialog` makes the same
 * economy: a **name** ("New folder", "Rename…", "Name this draft") and a
 * **choice** among the workspace's folders ("Move to folder…", "New
 * document"). Fluent `Dialog`, like every other modal in the pane. Resolves
 * through `onSubmit`; every other exit is `onCancel`, so the scene never waits
 * on a dialog that quietly closed.
 *
 * Looks like the library picker: a quiet eyebrow, a title, one line of help,
 * a bordered list of radio rows for a choice, and one primary action. A choice
 * is marked first and committed with the primary button; the option whose
 * value is ``createValue`` ("New folder…") is set apart below the list.
 *
 * A name may also carry a folder choice (``folders``), so naming a new draft
 * and filing it is one screen: the same radio list sits under the name, and
 * "+ New folder" opens a field in place instead of a second dialog.
 */

import {
  Button,
  Dialog,
  DialogActions,
  DialogBody,
  DialogContent,
  DialogSurface,
  DialogTitle,
  Input,
  makeStyles,
  mergeClasses,
} from "@fluentui/react-components";
import { type FC, useEffect, useState } from "react";
import { brandColors, fonts, inkColors, surfaceColors } from "../tokens";

export type HomePromptRequest =
  | {
      kind: "name";
      title: string;
      label: string;
      initial?: string;
      confirm?: string;
      /** Small context line above the title, e.g. "Clinical Study Report · US". */
      eyebrow?: string;
      placeholder?: string;
      /** One line under the field, e.g. that it can be renamed later. */
      hint?: string;
      /** Also choose a folder on the same screen. */
      folders?: { options: Array<{ value: string; label: string; detail?: string }>; initial: string };
    }
  | {
      kind: "choice";
      title: string;
      label: string;
      options: Array<{ value: string; label: string; detail?: string }>;
      eyebrow?: string;
      confirm?: string;
      /** Value preselected when the dialog opens. Defaults to the first option. */
      initial?: string;
      /** The option that creates something new; rendered apart, below the list. */
      createValue?: string;
    };

/** The folder picked alongside a name: a listed one, or a new one to create. */
export type HomePromptFolderPick = { value: string } | { newLabel: string };

const useStyles = makeStyles({
  surface: {
    maxWidth: "380px",
    width: "calc(100vw - 32px)",
    borderRadius: "8px",
    padding: "18px 18px 16px",
    border: `1px solid ${surfaceColors.line}`,
    boxShadow: "0 18px 40px rgba(15, 10, 30, 0.18)",
  },
  head: { display: "flex", flexDirection: "column", gap: "2px" },
  eyebrow: {
    fontFamily: fonts.body,
    fontSize: "11px",
    fontWeight: 500,
    color: inkColors.ink4,
  },
  title: {
    fontFamily: fonts.body,
    fontSize: "16px",
    fontWeight: 600,
    color: inkColors.ink,
    margin: 0,
  },
  body: { display: "flex", flexDirection: "column", gap: "10px", paddingTop: "4px" },
  help: {
    fontFamily: fonts.body,
    fontSize: "12px",
    lineHeight: 1.5,
    color: inkColors.ink3,
    margin: 0,
  },
  input: { width: "100%" },
  hint: { fontFamily: fonts.body, fontSize: "11px", color: inkColors.ink4, margin: 0 },
  list: {
    margin: 0,
    padding: 0,
    minWidth: 0,
    display: "flex",
    flexDirection: "column",
    border: `1px solid ${surfaceColors.line}`,
    borderRadius: "6px",
    backgroundColor: "#FFFFFF",
    maxHeight: "260px",
    overflowY: "auto",
  },
  option: {
    display: "flex",
    alignItems: "center",
    gap: "10px",
    width: "100%",
    fontFamily: fonts.body,
    fontSize: "13px",
    color: inkColors.ink,
    textAlign: "left",
    background: "transparent",
    border: "none",
    borderTop: `1px solid ${surfaceColors.line}`,
    padding: "9px 12px",
    cursor: "pointer",
    ":first-child": { borderTopColor: "transparent" },
    ":hover": { backgroundColor: surfaceColors.paper },
    ":focus-visible": { outline: `2px solid ${brandColors.primary}`, outlineOffset: "-2px" },
  },
  optionOn: { fontWeight: 600 },
  optionText: { display: "flex", flexDirection: "column", gap: "1px", minWidth: 0 },
  optionLabel: { overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" },
  optionDetail: { fontSize: "11px", fontWeight: 400, color: inkColors.ink4 },
  radio: {
    width: "14px",
    height: "14px",
    borderRadius: "50%",
    border: `1.5px solid ${inkColors.ink5}`,
    boxSizing: "border-box",
    flexShrink: 0,
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
  },
  radioOn: {
    borderTopColor: brandColors.primary,
    borderRightColor: brandColors.primary,
    borderBottomColor: brandColors.primary,
    borderLeftColor: brandColors.primary,
    "::after": {
      content: '""',
      width: "6px",
      height: "6px",
      borderRadius: "50%",
      backgroundColor: brandColors.primary,
    },
  },
  create: {
    alignSelf: "flex-start",
    background: "transparent",
    border: "none",
    padding: "2px 0",
    fontFamily: fonts.body,
    fontSize: "12px",
    fontWeight: 600,
    color: inkColors.ink2,
    cursor: "pointer",
    ":hover": { textDecoration: "underline" },
    ":focus-visible": { outline: `2px solid ${brandColors.primary}`, outlineOffset: "2px" },
  },
  section: {
    fontFamily: fonts.body,
    fontSize: "11px",
    fontWeight: 600,
    color: inkColors.ink3,
    margin: "6px 0 0",
  },
  shortList: { maxHeight: "168px" },
  newFolder: { display: "flex", flexDirection: "column", gap: "8px" },
  inlineLink: { whiteSpace: "nowrap", flexShrink: 0 },
  primary: {
    backgroundColor: brandColors.primary,
    ":hover": { backgroundColor: brandColors.primary, opacity: 0.92 },
  },
});

export const HomePromptDialog: FC<{
  request: HomePromptRequest | null;
  onSubmit: (value: string, folder?: HomePromptFolderPick) => void;
  onCancel: () => void;
}> = ({ request, onSubmit, onCancel }) => {
  const styles = useStyles();
  const [value, setValue] = useState("");
  const [folder, setFolder] = useState("");
  // Non-null while "+ New folder" is open on a name screen: the new folder's name.
  const [newFolder, setNewFolder] = useState<string | null>(null);
  useEffect(() => {
    setFolder(request?.kind === "name" ? (request.folders?.initial ?? "") : "");
    setNewFolder(null);
    if (request?.kind === "name") setValue(request.initial ?? "");
    else if (request?.kind === "choice") {
      const listed = request.options.filter((o) => o.value !== request.createValue);
      setValue(request.initial ?? listed[0]?.value ?? "");
    } else setValue("");
  }, [request]);
  const open = request !== null;
  const trimmed = value.trim();
  const listed = request?.kind === "choice" ? request.options.filter((o) => o.value !== request.createValue) : [];
  // A choice may legitimately be "" (e.g. "No folder"), so it is valid when
  // it names a listed option; a name must be non-blank and is trimmed.
  const folders = request?.kind === "name" ? request.folders : undefined;
  const folderOk =
    !folders || (newFolder === null ? folders.options.some((o) => o.value === folder) : newFolder.trim().length > 0);
  const canSubmit = request?.kind === "choice" ? listed.some((o) => o.value === value) : trimmed.length > 0 && folderOk;
  const submitValue = request?.kind === "choice" ? value : trimmed;
  const folderPick: HomePromptFolderPick | undefined = !folders
    ? undefined
    : newFolder !== null
      ? { newLabel: newFolder.trim() }
      : { value: folder };
  const createOption =
    request?.kind === "choice" && request.createValue
      ? (request.options.find((o) => o.value === request.createValue) ?? null)
      : null;

  return (
    <Dialog
      open={open}
      onOpenChange={(_, data) => {
        if (!data.open) onCancel();
      }}
    >
      <DialogSurface className={styles.surface}>
        <DialogBody>
          <DialogTitle>
            <span className={styles.head}>
              {request?.eyebrow && <span className={styles.eyebrow}>{request.eyebrow}</span>}
              <span className={styles.title}>{request?.title ?? ""}</span>
            </span>
          </DialogTitle>
          <DialogContent>
            <form
              id="home-prompt-form"
              className={styles.body}
              onSubmit={(e) => {
                e.preventDefault();
                if (!canSubmit) return;
                if (folderPick) onSubmit(submitValue, folderPick);
                else onSubmit(submitValue);
              }}
            >
              <p className={styles.help}>{request?.label}</p>
              {request?.kind === "name" && (
                <>
                  <Input
                    className={styles.input}
                    value={value}
                    maxLength={120}
                    aria-label={request.label}
                    placeholder={request.placeholder}
                    onChange={(_, data) => setValue(data.value)}
                    autoFocus
                  />
                  {request.hint && <p className={styles.hint}>{request.hint}</p>}
                  {request.folders && (
                    <>
                      <p className={styles.section}>{newFolder === null ? "Folder" : "New folder"}</p>
                      {newFolder === null ? (
                        <>
                          <fieldset className={mergeClasses(styles.list, styles.shortList)} aria-label="Folder">
                            {request.folders.options.map((option) => {
                              const on = option.value === folder;
                              return (
                                <button
                                  type="button"
                                  // biome-ignore lint/a11y/useSemanticElements: a styled radio row (a native radio cannot take this layout)
                                  role="radio"
                                  aria-checked={on}
                                  key={option.value}
                                  className={mergeClasses(styles.option, on && styles.optionOn)}
                                  onClick={() => setFolder(option.value)}
                                >
                                  <span
                                    className={mergeClasses(styles.radio, on && styles.radioOn)}
                                    aria-hidden="true"
                                  />
                                  <span className={styles.optionText}>
                                    <span className={styles.optionLabel}>{option.label}</span>
                                    {option.detail && <span className={styles.optionDetail}>{option.detail}</span>}
                                  </span>
                                </button>
                              );
                            })}
                          </fieldset>
                          <button type="button" className={styles.create} onClick={() => setNewFolder("")}>
                            + New folder
                          </button>
                        </>
                      ) : (
                        <div className={styles.newFolder}>
                          <Input
                            className={styles.input}
                            value={newFolder}
                            maxLength={120}
                            aria-label="New folder name"
                            placeholder="e.g. SNG-208 Phase 1"
                            onChange={(_, data) => setNewFolder(data.value)}
                            autoFocus
                          />
                          <button
                            type="button"
                            className={mergeClasses(styles.create, styles.inlineLink)}
                            onClick={() => setNewFolder(null)}
                          >
                            Choose an existing folder
                          </button>
                        </div>
                      )}
                    </>
                  )}
                </>
              )}
              {request?.kind === "choice" && (
                <>
                  <fieldset className={styles.list} aria-label={request.label}>
                    {listed.map((option) => {
                      const on = option.value === value;
                      return (
                        <button
                          type="button"
                          // biome-ignore lint/a11y/useSemanticElements: a styled radio row (a native radio cannot take this layout)
                          role="radio"
                          aria-checked={on}
                          key={option.value}
                          className={mergeClasses(styles.option, on && styles.optionOn)}
                          onClick={() => setValue(option.value)}
                          onDoubleClick={() => onSubmit(option.value)}
                        >
                          <span className={mergeClasses(styles.radio, on && styles.radioOn)} aria-hidden="true" />
                          <span className={styles.optionText}>
                            <span className={styles.optionLabel}>{option.label}</span>
                            {option.detail && <span className={styles.optionDetail}>{option.detail}</span>}
                          </span>
                        </button>
                      );
                    })}
                  </fieldset>
                  {createOption && (
                    <button type="button" className={styles.create} onClick={() => onSubmit(createOption.value)}>
                      + {createOption.label.replace(/…$/, "")}
                    </button>
                  )}
                </>
              )}
            </form>
          </DialogContent>
          <DialogActions>
            <Button appearance="secondary" onClick={onCancel}>
              Cancel
            </Button>
            <Button
              appearance="primary"
              className={styles.primary}
              type="submit"
              form="home-prompt-form"
              disabled={!canSubmit}
            >
              {request?.confirm ?? (request?.kind === "choice" ? "Continue" : "Save")}
            </Button>
          </DialogActions>
        </DialogBody>
      </DialogSurface>
    </Dialog>
  );
};

export default HomePromptDialog;
